/* global process */

// import logo from "./logo.svg";
// import backgroundBlob from "./static/background-blob.svg";
import "./App.css";
import { useEffect, useRef, useState } from "react";
import Welcome from "./components/layouts/Welcome";
import Login from "./components/layouts/Login";
import PageNotFound from "./components/layouts/PageNotFound";
import ScreenWrapper from "./components/layouts/ScreenWrapper";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import PrivateRoutes from "./components/PrivateRoutes";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence } from "framer-motion";
import { ToastProvider } from "./contexts/ToastContext";
import useGetApi from "./util/useGetApi";
import { useAppStore } from "./store/store";

library.add(fas, far);

function getRuntimeConfigUrl() {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  console.log("Fetching config from ", publicUrl);

  return publicUrl ? `${publicUrl}/config.json` : "/config.json";
}

const ALLOWED_RUNTIME_CONFIG_KEYS = [
  "API_ENDPOINT",
  "RECAPTCHA_SITE_KEY",
];

function applyRuntimeConfig(config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return;
  }

  ALLOWED_RUNTIME_CONFIG_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(config, key)) {
      window[key] = config[key];
    }
  });
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isConfigReady, setIsConfigReady] = useState(false);
  const [isSessionChecked, setIsSessionChecked] = useState(false);
  const bootstrapped = useRef(false);
  const { bootstrapSession } = useGetApi();

  useEffect(() => {
    let isMounted = true;

    async function loadRuntimeConfig() {
      try {
        const response = await fetch(getRuntimeConfigUrl(), {
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `Runtime config request failed with ${response.status}`,
          );
        }

        const config = await response.json();
        applyRuntimeConfig(config);
      } catch (error) {
        console.error("Failed to load runtime config", error);
      } finally {
        if (isMounted) {
          setIsConfigReady(true);
        }
      }
    }

    loadRuntimeConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  // After config is ready, try to restore an existing session via the
  // refresh-token cookie. Wait for this to settle before rendering routes
  // so PrivateRoutes doesn't bounce a refresh-page user to /login first.
  // The ref guard is critical: refresh-token rotation includes reuse-detection
  // that revokes all of a user's sessions if the same token is presented twice.
  // React StrictMode (dev) runs effects twice and would trip that alarm.
  useEffect(() => {
    if (!isConfigReady || bootstrapped.current) return;
    bootstrapped.current = true;
    let isMounted = true;
    (async () => {
      const ok = await bootstrapSession();
      if (!isMounted) return;
      setIsSessionChecked(true);
      const path = window.location.pathname;
      const onScreenRoute = /\/screen\/\d+\.\d+/.test(path);
      if (ok) {
        // Authenticated. A direct /screen/* deep link (refresh, bookmark,
        // browser back/forward) keeps the user where they were. Anything
        // else — a fresh magic-link redeem landing on /form/, root —
        // starts them at the beginning of the flow: returning users with
        // a saved selection go to 1.11 ("Welcome back, you're currently
        // in X"), first-timers go to 1.5 (age-based recco).
        if (!onScreenRoute) {
          const hasPortfolio = !!useAppStore.getState().user?.portfolioId;
          navigate(hasPortfolio ? "/screen/1.11" : "/screen/1.5", {
            replace: true,
          });
        }
      }
      // Unauthenticated: render the requested route as-is. /form shows the
      // Welcome page (its Continue button takes them to /login from there).
    })();
    return () => {
      isMounted = false;
    };
    // bootstrapSession is stable across renders; we only want this once after config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigReady]);

  if (!isConfigReady || !isSessionChecked) {
    return null;
  }

  return (
    <ToastProvider>
      <div className="flex items-start min-h-[calc(100vh-84px)] lg:min-h-[calc(100vh-97px)] pt-3 pb-10 overflow-hidden md:pb-16 md:pt-6 bg-as-blue-0">
        <div className="overflow-visible wrapper">
          <div className="relative z-10 flex min-h-[41.5625rem] w-full overflow-hidden rounded-md border border-as-blue-50 bg-white">
            <AnimatePresence mode="wait" initial={false}>
              <Routes location={location} key={location.pathname}>
                <Route element={<PrivateRoutes />}>
                  <Route path="/screen/:screenId" element={<ScreenWrapper />} />
                </Route>

                <Route path="/form" element={<Welcome />} />
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Login />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </ToastProvider>
  );
}

export default App;
